import React, { useState } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import UserAccountMenu from '../../components/ui/UserAccountMenu';
import Icon from '../../components/AppIcon';

const BestPractices = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const hookExamples = [
    "Why is identifying the tumor bed after surgery so difficult?",
    "This is where radiation planning can lose accuracy.",
    "Most patients don't realize how much this step matters.",
    "Lumpectomy patients shouldn\'t have to be reminded daily about their cancer"
  ];

  const visualHookExamples = [
    { title: "Just got out of the OR", description: "In or outside of the OR removing scrub cap as you start talking" },
    { title: "Addressing something unusual", description: "Close up of the confusing visual filling the frame" },
    { title: "Comparing past vs current", description: "Clips visible in hand before being removed from frame and showing Videra" },
    { title: "Explaining precision", description: "Tight, unlabeled zoom on imaging (mammogram or CT)" },
    { title: "Correcting a misconception", description: "Visible head shake before any explanation" },
    { title: "Introducing the device", description: "Device partially in frame, not yet fully revealed" }
  ];

  const suspenseExamples = [
    "With oncoplastic surgery, tissue mobilization can make localization challenging.",
    "Without a clear reference point, planning becomes less precise.",
    "That uncertainty can affect confidence in treatment delivery."
  ];

  const payoutExamples = [
    "A continuous 3D marker clearly defines the tumor bed across imaging using TrueForm by Videra.",
    "Clear visualization allows for more confident radiation planning thanks to TrueForm by Videra.",
    "Support precise, patient-centered care with TrueForm by Videra."
  ];

  const contentThemes = [
    "Tumor bed visualization after lumpectomy",
    "Precision in radiation planning",
    "Confidence for radiation oncologists",
    "Patient-friendly, non-palpable solutions",
    "Advantages over traditional clips"
  ];

  const filmingReminders = [
    { icon: "Smartphone", text: "Always shoot in vertical format (9:16)" },
    { icon: "Sun", text: "Keep lighting clean and natural (ring light or softbox if indoors)" },
    { icon: "Lightbulb", text: "If you do not have a light, use natural light by facing a window" },
    { icon: "Video", text: "Capture multiple takes for flexibility in editing" },
    { icon: "Mic", text: "Prioritize clear audio" },
    { icon: "Type", text: "Leave intentional space for captions or on-screen text" }
  ];

  const editingTools = [
    { name: "Script Reader", tool: "Teleprompter app", purpose: "Maintain eye contact while speaking" },
    { name: "Editor", tool: "CapCut", purpose: "Trimming, basic text overlays, and exports" },
    { name: "Captions", tool: "Opus clip", purpose: "Add a brand font template for consistency" },
    { name: "Scheduling", tool: "Sprout Social", purpose: "Keeps all posting, approvals, and performance insights in one place" }
  ];

  const equipment = [
    { icon: "Smartphone", item: "Smartphone with a strong camera", priority: "Essential" },
    { icon: "Mic", item: "Clip on microphone", priority: "Recommended", benefit: "Improves audio clarity" },
    { icon: "Camera", item: "Phone stand or small tripod", priority: "Recommended", benefit: "Keeps framing steady and professional" },
    { icon: "Sun", item: "Natural light or ring light", priority: "Essential", benefit: "If natural light is not available" }
  ];

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-60">
        <header className="bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-light uppercase text-foreground">Best Practices</h1>
            <p className="text-sm text-muted-foreground mt-1">Expert guidance for high-performing social media content</p>
          </div>
          <UserAccountMenu />
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-lg border border-border shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-primary/5 to-accent/5 px-6 py-5 border-b border-border">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Icon name="Video" size={28} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-light uppercase text-foreground mb-2">Social Media Best Practices</h2>
                    <p className="text-sm text-muted-foreground font-caption">How to create high-performing educational content</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Hook-Suspense-Payout Framework */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-1 w-12 bg-primary rounded"></div>
                    <h3 className="text-lg font-medium uppercase text-foreground">The Hook → Suspense → Payout Framework</h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    High-performing medical content follows a simple structure that keeps viewers watching while maintaining clinical credibility.
                  </p>

                  {/* Hook Section */}
                  <div className="border border-border rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection('hook')}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white text-sm font-semibold">
                          1
                        </div>
                        <div className="text-left">
                          <h4 className="font-medium text-foreground">HOOK [0:01-0:03]</h4>
                          <p className="text-xs text-muted-foreground">Stop the scroll immediately</p>
                        </div>
                      </div>
                      <Icon name={expandedSection === 'hook' ? 'ChevronUp' : 'ChevronDown'} size={20} />
                    </button>
                    {expandedSection === 'hook' && (
                      <div className="p-4 space-y-4 bg-white">
                        <p className="text-sm text-muted-foreground">
                          The hook appears in the first 1–3 seconds and creates curiosity or surprise.
                        </p>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <h5 className="text-sm font-semibold text-success flex items-center gap-2">
                              <Icon name="Check" size={16} /> Best Practices
                            </h5>
                            <ul className="text-sm text-muted-foreground space-y-1 pl-4">
                              <li className="list-disc">Lead with a question or bold statement</li>
                              <li className="list-disc">Challenge a common assumption</li>
                              <li className="list-disc">Focus on what most people don't realize</li>
                            </ul>
                          </div>
                          <div className="space-y-2">
                            <h5 className="text-sm font-semibold text-destructive flex items-center gap-2">
                              <Icon name="X" size={16} /> Avoid
                            </h5>
                            <ul className="text-sm text-muted-foreground space-y-1 pl-4">
                              <li className="list-disc">Introductions ("Hi, I'm Dr…")</li>
                              <li className="list-disc">Brand names</li>
                              <li className="list-disc">Full explanations</li>
                              <li className="list-disc">Clinical heavy lingo</li>
                              <li className="list-disc">Long pauses</li>
                            </ul>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <h5 className="text-sm font-semibold text-foreground">Videra-Aligned Verbal Hook Examples:</h5>
                          <div className="grid gap-2">
                            {hookExamples?.map((example, index) => (
                              <div key={index} className="flex items-start gap-2 p-3 bg-primary/5 rounded-lg">
                                <Icon name="MessageSquare" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-foreground italic">"{example}"</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-3">
                          <h5 className="text-sm font-semibold text-foreground">Videra-Aligned Visual Hook Examples:</h5>
                          <div className="grid md:grid-cols-2 gap-3">
                            {visualHookExamples?.map((example, index) => (
                              <div key={index} className="p-3 border border-border rounded-lg">
                                <h6 className="text-sm font-medium text-foreground mb-1">{example?.title}</h6>
                                <p className="text-xs text-muted-foreground">{example?.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Suspense Section */}
                  <div className="border border-border rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection('suspense')}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-accent text-white text-sm font-semibold">
                          2
                        </div>
                        <div className="text-left">
                          <h4 className="font-medium text-foreground">SUSPENSE [0:03-0:30]</h4>
                          <p className="text-xs text-muted-foreground">Build context without giving the answer</p>
                        </div>
                      </div>
                      <Icon name={expandedSection === 'suspense' ? 'ChevronUp' : 'ChevronDown'} size={20} />
                    </button>
                    {expandedSection === 'suspense' && (
                      <div className="p-4 space-y-4 bg-white">
                        <p className="text-sm text-muted-foreground">
                          Suspense keeps viewers engaged by adding stakes and setting up the problem.
                        </p>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <h5 className="text-sm font-semibold text-success flex items-center gap-2">
                              <Icon name="Check" size={16} /> Best Practices
                            </h5>
                            <ul className="text-sm text-muted-foreground space-y-1 pl-4">
                              <li className="list-disc">Add clinical context</li>
                              <li className="list-disc">Highlight why the issue matters for patients</li>
                              <li className="list-disc">Intentionally delay the solution</li>
                            </ul>
                          </div>
                          <div className="space-y-2">
                            <h5 className="text-sm font-semibold text-destructive flex items-center gap-2">
                              <Icon name="X" size={16} /> Avoid
                            </h5>
                            <ul className="text-sm text-muted-foreground space-y-1 pl-4">
                              <li className="list-disc">Explaining the solution too early</li>
                              <li className="list-disc">Listing benefits too soon</li>
                            </ul>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <h5 className="text-sm font-semibold text-foreground">Videra-Aligned Suspense Examples:</h5>
                          <div className="grid gap-2">
                            {suspenseExamples?.map((example, index) => (
                              <div key={index} className="flex items-start gap-2 p-3 bg-accent/5 rounded-lg">
                                <Icon name="Clock" size={16} className="text-accent mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-foreground italic">"{example}"</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Payout Section */}
                  <div className="border border-border rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection('payout')}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-success text-white text-sm font-semibold">
                          3
                        </div>
                        <div className="text-left">
                          <h4 className="font-medium text-foreground">PAYOUT [Last Few Seconds]</h4>
                          <p className="text-xs text-muted-foreground">Deliver the value they stayed for</p>
                        </div>
                      </div>
                      <Icon name={expandedSection === 'payout' ? 'ChevronUp' : 'ChevronDown'} size={20} />
                    </button>
                    {expandedSection === 'payout' && (
                      <div className="p-4 space-y-4 bg-white">
                        <p className="text-sm text-muted-foreground">
                          The payout provides the explanation, insight, or solution and connects directly to Videra's expertise.
                        </p>

                        <div className="space-y-2">
                          <h5 className="text-sm font-semibold text-success flex items-center gap-2">
                            <Icon name="Check" size={16} /> Best Practices
                          </h5>
                          <ul className="text-sm text-muted-foreground space-y-1 pl-4">
                            <li className="list-disc">Clearly explain why the solution works</li>
                            <li className="list-disc">Keep language simple but clinically accurate</li>
                            <li className="list-disc">Tie back to patient outcomes and workflow</li>
                            <li className="list-disc">End immediately after saying "Videra" (critical for algorithm)</li>
                          </ul>
                        </div>

                        <div className="space-y-3">
                          <h5 className="text-sm font-semibold text-foreground">Videra-Aligned Payout Examples:</h5>
                          <div className="grid gap-2">
                            {payoutExamples?.map((example, index) => (
                              <div key={index} className="flex items-start gap-2 p-3 bg-success/5 rounded-lg">
                                <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-foreground italic">"{example}"</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <h5 className="text-sm font-semibold text-blue-900 mb-2">Why This Works for Videra:</h5>
                          <p className="text-sm text-blue-800 mb-3">
                            Videra's technology solves a real, clinically relevant problem. Social content performs best when it:
                          </p>
                          <ul className="text-sm text-blue-800 space-y-1 pl-4">
                            <li className="list-disc">Educates without overwhelming</li>
                            <li className="list-disc">Builds trust through clarity</li>
                            <li className="list-disc">Shows value through visualization and outcomes</li>
                            <li className="list-disc">Storytells</li>
                          </ul>
                          <p className="text-sm text-blue-800 mt-3 font-medium">
                            The hook captures attention. The suspense builds importance. The payout delivers understanding.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Content Themes */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="h-1 w-12 bg-accent rounded"></div>
                    <h3 className="text-lg font-medium uppercase text-foreground">Key Content Themes for Videra</h3>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    {contentThemes?.map((theme, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-accent/5 border border-accent/20 rounded-lg">
                        <Icon name="Target" size={16} className="text-accent mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-foreground">{theme}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Filming Reminders */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="h-1 w-12 bg-primary rounded"></div>
                    <h3 className="text-lg font-medium uppercase text-foreground">Key Filming Reminders (Carty Media Standard)</h3>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    {filmingReminders?.map((reminder, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 border border-border rounded-lg">
                        <Icon name={reminder?.icon} size={18} className="text-primary flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-foreground">{reminder?.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Editing Tools */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="h-1 w-12 bg-success rounded"></div>
                    <h3 className="text-lg font-medium uppercase text-foreground">Editing Tools</h3>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {editingTools?.map((tool, index) => (
                      <div key={index} className="p-4 bg-white border border-border rounded-lg">
                        <h5 className="text-sm font-semibold text-foreground mb-1">{tool?.name}</h5>
                        <p className="text-sm text-primary font-medium mb-2">{tool?.tool}</p>
                        <p className="text-xs text-muted-foreground">{tool?.purpose}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Suggested Equipment */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="h-1 w-12 bg-accent rounded"></div>
                    <h3 className="text-lg font-medium uppercase text-foreground">Suggested Equipment</h3>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {equipment?.map((item, index) => (
                      <div key={index} className="p-4 bg-white border border-border rounded-lg">
                        <div className="flex items-start gap-3 mb-2">
                          <Icon name={item?.icon} size={20} className="text-accent flex-shrink-0" />
                          <div className="flex-1">
                            <h5 className="text-sm font-semibold text-foreground">{item?.item}</h5>
                            <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                              item?.priority === 'Essential' ?'bg-success/10 text-success' :'bg-blue-100 text-blue-700'
                            }`}>
                              {item?.priority}
                            </span>
                          </div>
                        </div>
                        {item?.benefit && (
                          <p className="text-xs text-muted-foreground mt-2">{item?.benefit}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default BestPractices;